import React, { useEffect, useMemo, useState, useCallback } from "react";
import { flushSync } from "react-dom";
import DICOMViewer from "./DICOMViewer";
import { useViewer } from "./context/ViewerContext";
import spinner from "../../../assets/spiner.svg";

/** helpers */
function ordinal(n) {
  const s = ["th", "st", "nd", "rd"];
  const v = n % 100;
  return n + (s[(v - 20) % 10] || s[v] || s[0]);
}

function parseDateLoose(d) {
  if (!d) return null;
  // Support: ISO, Date, "YYYYMMDD", "YYYY-MM-DD"
  if (d instanceof Date) return d;
  if (typeof d === "number") return new Date(d);
  if (typeof d === "string") {
    const s = d.trim();
    if (/^\d{8}$/.test(s)) {
      // YYYYMMDD
      const y = +s.slice(0, 4);
      const m = +s.slice(4, 6) - 1;
      const day = +s.slice(6, 8);
      return new Date(y, m, day);
    }
    const asDate = new Date(s);
    if (!isNaN(asDate.getTime())) return asDate;
  }
  return null;
}

function formatAccqDate(dateLike) {
  const dt = parseDateLoose(dateLike);
  if (!dt) return "—";
  const day = ordinal(dt.getDate());
  const month = dt.toLocaleString("en-US", { month: "long" });
  const year = dt.getFullYear();
  // visual matches: "25th, September 2025"
  return `${day}, ${month} ${year}`;
}

export default function MultiViewDICOM({
  exams = [],
  currentUser,
  topLeftIndex,
  setTopLeftIndex,
}) {
  const tiles = useMemo(() => {
    if (!Array.isArray(exams)) return [];
    const count = Math.min(4, exams.length);
    return Array.from({ length: count }, (_, i) => exams[i]); // preserve placeholders
  }, [exams]);

  // global zoom from context (when it changes we clear local overrides)
  const { zoom: globalZoom, scrollLinked } = useViewer();

  const [indices, setIndices] = useState(() =>
    Object.fromEntries(tiles.map((_, i) => [`slot-${i}`, 0]))
  );
  const setIdx = (key, v) => setIndices((prev) => ({ ...prev, [key]: v }));

  // Use refs to track current indices for linked scrolling
  const topLeftIndexRef = React.useRef(topLeftIndex);
  const indicesRef = React.useRef(indices);
  
  // Keep refs in sync with state
  React.useEffect(() => {
    topLeftIndexRef.current = topLeftIndex;
  }, [topLeftIndex]);
  
  React.useEffect(() => {
    indicesRef.current = indices;
  }, [indices]);

  // Create linked scroll handler for each tile
  const createLinkedScrollHandler = useCallback((tileIndex, maxIndex, originalSetIndex) => {
    return (newIndex) => {
      if (!scrollLinked) {
        // Normal behavior when not linked
        originalSetIndex(newIndex);
        return;
      }

      // Get current indices from refs (always up-to-date)
      const currentTopLeftIndex = topLeftIndexRef.current;
      const currentIndices = indicesRef.current;
      
      // Get the current index of the source tile
      const currentIndex = tileIndex === 0 ? currentTopLeftIndex : currentIndices[`slot-${tileIndex}`] ?? 0;
      
      // Clamp newIndex to valid range for source tile
      const clampedNewIndex = Math.max(0, Math.min(newIndex, maxIndex));
      
      // Calculate delta (requested change, not clamped)
      const requestedDelta = newIndex - currentIndex;
      
      // Calculate all new indices first (before applying any updates)
      const newIndices = { ...currentIndices };
      let newTopLeftIndex = currentTopLeftIndex;
      
      // Update source tile index
      if (tileIndex === 0) {
        newTopLeftIndex = clampedNewIndex;
      } else {
        newIndices[`slot-${tileIndex}`] = clampedNewIndex;
      }
      
      // Calculate new indices for all other tiles
      tiles.forEach((exam, i) => {
        if (i === tileIndex) return; // Skip the source tile
        if (!exam) return; // Skip null/undefined exams
        
        const otherMaxIndex = exam?.dicomFiles?.length ? exam.dicomFiles.length - 1 : 0;
        const otherKey = `slot-${i}`;
        const otherCurrentIndex = i === 0 ? currentTopLeftIndex : currentIndices[otherKey] ?? 0;
        
        // Calculate new index for this tile using the requested delta
        // This allows others to continue scrolling even if source hit max
        let otherNewIndex = otherCurrentIndex + requestedDelta;
        
        // Clamp to valid range
        otherNewIndex = Math.max(0, Math.min(otherNewIndex, otherMaxIndex));
        
        // Store the new index
        if (i === 0) {
          newTopLeftIndex = otherNewIndex;
        } else {
          newIndices[otherKey] = otherNewIndex;
        }
      });
      
      // Apply all updates simultaneously using flushSync for synchronous rendering
      // This ensures all tiles update at exactly the same time with no delay
      flushSync(() => {
        // Update topLeftIndex if it changed
        if (newTopLeftIndex !== currentTopLeftIndex) {
          setTopLeftIndex(newTopLeftIndex);
        }
        
        // Update all slot indices in a single batch
        setIndices((prev) => {
          const updated = { ...prev };
          let hasChanges = false;
          
          tiles.forEach((exam, i) => {
            if (i === 0) return; // topLeftIndex is handled separately
            const key = `slot-${i}`;
            if (newIndices[key] !== undefined && newIndices[key] !== prev[key]) {
              updated[key] = newIndices[key];
              hasChanges = true;
            }
          });
          
          return hasChanges ? updated : prev;
        });
      });
    };
  }, [scrollLinked, tiles, setTopLeftIndex, setIdx]);

  // Per-tile zoom override; null means "follow global"
  const [zoomMap, setZoomMap] = useState(() =>
    Object.fromEntries(tiles.map((_, i) => [`slot-${i}`, null]))
  );

  // ensure new tiles get a default entry
  useEffect(() => {
    setIndices((prev) => {
      const next = { ...prev };
      tiles.forEach((_, i) => {
        const key = `slot-${i}`;
        if (!(key in next)) next[key] = 0;
      });
      return next;
    });
    setZoomMap((prev) => {
      const next = { ...prev };
      tiles.forEach((_, i) => {
        const key = `slot-${i}`;
        if (!(key in next)) next[key] = null;
      });
      return next;
    });
  }, [tiles]);

  // When scrollLinked becomes true, sync all tiles to the same index
  useEffect(() => {
    if (!scrollLinked) return;
    if (tiles.length <= 1) return;

    // Use the first tile's index as the reference
    const referenceIndex = topLeftIndex;
    
    // Sync all other tiles to the reference index
    tiles.forEach((exam, i) => {
      if (i === 0) return; // Skip the first tile (reference)
      if (!exam) return; // Skip null/undefined exams
      
      const maxIndex = exam?.dicomFiles?.length ? exam.dicomFiles.length - 1 : 0;
      // Clamp to valid range for this tile
      const syncedIndex = Math.min(referenceIndex, maxIndex);
      
      const key = `slot-${i}`;
      setIdx(key, syncedIndex);
    });
  }, [scrollLinked, tiles, topLeftIndex, setIdx]);

  // When global zoom changes, reset all local overrides to follow it
  useEffect(() => {
    setZoomMap((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        next[k] = null;
      });
      return next;
    });
  }, [globalZoom]);

  const gridClass =
    tiles.length === 1
      ? "grid-cols-1 grid-rows-1"
      : tiles.length === 2
      ? "grid-cols-2 grid-rows-1"
      : "grid-cols-2 grid-rows-2";

  return (
    <div
      className={`grid ${gridClass} gap-2 p-2 w-full h-full min-w-0 min-h-0`}>
      {tiles.map((exam, i) => {
        const key = `slot-${i}`;
        const idx = i === 0 ? topLeftIndex : indices[key] ?? 0;
        const maxIndex = exam?.dicomFiles?.length ? exam.dicomFiles.length - 1 : 0;
        const originalSetIndex = i === 0 ? setTopLeftIndex : (v) => setIdx(key, v);
        const setIndex = scrollLinked 
          ? createLinkedScrollHandler(i, maxIndex, originalSetIndex)
          : originalSetIndex;
        const localTileZoom = zoomMap[key];
        const selectValue = String(localTileZoom ?? globalZoom);

        // Header fields
        const caseId = exam?.caseId ?? "N/A";
        const risId = exam?.patientId ?? "N/A";
        const dateRaw = exam?.createdAt;
        const dateLabel = formatAccqDate(dateRaw);
        const slideLabel = `Slide ${idx + 1}`;

        return (
          <div
            key={key}
            className="relative bg-black rounded overflow-hidden w-full h-full min-w-0 min-h-0 border border-[#2B2B2B]">
            {/* Header (black) */}
            <div className="flex items-center justify-between px-4 h-11 bg-black text-white border-b border-[#2B2B2B]">
              {/* Left: Slide | Date | Case - RIS */}
              <div className="flex items-center gap-3 text-[13px] leading-none">
                <span className="opacity-95">{slideLabel}</span>
                <span className="opacity-40">|</span>
                <span className="opacity-95">{dateLabel}</span>
                <span className="opacity-40">|</span>
                <span className="opacity-95">
                  Case {caseId} - RIS {risId}
                </span>
              </div>

              {/* Right: per-tile Zoom dropdown */}
              <div className="flex items-center gap-2">
                <select
                  className="text-xs px-2 py-1 rounded bg-[#151515] text-white border border-[#2B2B2B] outline-none"
                  value={selectValue}
                  onChange={(e) => {
                    const v = Number(e.target.value);
                    setZoomMap((prev) => ({
                      ...prev,
                      [key]: isNaN(v) ? null : v,
                    }));
                  }}>
                  <option value={1}>100%</option>
                  <option value={1.25}>125%</option>
                  <option value={1.5}>150%</option>
                  <option value={2}>200%</option>
                  <option value={2.5}>250%</option>
                  <option value={3}>300%</option>
                </select>
              </div>
            </div>

            {/* Content area with clear divider from header */}
            <div className="absolute inset-x-0 bottom-0 top-11">
              {exam ? (
                <DICOMViewer
                  examData={exam}
                  currentUser={currentUser}
                  currentImageIndex={idx}
                  setCurrentImageIndex={setIndex}
                  notesOpen={false}
                  localZoom={localTileZoom ?? undefined}
                />
              ) : (
                <TileSkeleton label="Loading prior exam…" />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function TileSkeleton({ label = "Loading…" }) {
  return (
    <div className="absolute inset-0 flex items-center justify-center z-20">
      <div className="flex flex-col items-center gap-2 text-gray-300 animate-pulse">
        <img
          src={spinner}
          alt="Loading…"
          className="h-16 w-16 animate-spin filter invert block"
        />
        <div className="text-sm">{label}</div>
      </div>
    </div>
  );
}
